import React from "react";
import { AlertCircle, Inbox } from "lucide-react";
import { neutral } from "../../theme/palette";

// ---------- layout ----------

export const Section = ({ title, description, children, actions }) => (
  <section className="space-y-4">
    <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h2 className="text-lg font-semibold tracking-tight text-fg">{title}</h2>
        {description && <p className="mt-0.5 text-13 text-muted">{description}</p>}
      </div>
      {actions}
    </div>
    {children}
  </section>
);

// Card that owns its loading / error / empty state, so one failing source never blanks the page
export const ChartCard = ({
  title,
  description,
  actions,
  isLoading,
  error,
  isEmpty,
  emptyText = "No data for the selected period",
  height = 260,
  className = "",
  children,
}) => (
  <div className={`card flex flex-col p-5 ${className}`}>
    {(title || actions) && (
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {title && <h3 className="section-title">{title}</h3>}
          {description && <p className="mt-0.5 text-13 text-muted">{description}</p>}
        </div>
        {actions}
      </div>
    )}
    <div className="relative flex-1" style={{ minHeight: height }}>
      {isLoading ? (
        <ChartSkeleton height={height} />
      ) : error ? (
        <StateMessage icon={AlertCircle} tone="error" text="Couldn't load this data" detail={error} />
      ) : isEmpty ? (
        <StateMessage icon={Inbox} text={emptyText} />
      ) : (
        children
      )}
    </div>
  </div>
);

export const StateMessage = ({ icon: Icon, text, detail, tone }) => (
  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center">
    <div className={`flex h-10 w-10 items-center justify-center rounded-full ${tone === "error" ? "bg-red-50 text-red-600" : "bg-neutral-100 text-neutral-400"}`}>
      <Icon className="h-5 w-5" />
    </div>
    <p className="text-13 font-medium text-neutral-700">{text}</p>
    {detail && <p className="max-w-xs text-xs text-muted">{detail}</p>}
  </div>
);

// ---------- skeletons ----------

export const Skeleton = ({ className = "" }) => <div className={`animate-pulse rounded-md bg-neutral-200/70 ${className}`} />;

export const ChartSkeleton = ({ height = 260 }) => {
  const bars = [45, 70, 55, 85, 60, 75, 40, 90, 65, 50, 80, 58];
  return (
    <div className="flex h-full items-end gap-2 pb-6" style={{ height }}>
      {bars.map((h, i) => (
        <div key={i} className="flex-1 animate-pulse rounded-t bg-neutral-200/70" style={{ height: `${h}%`, animationDelay: `${i * 60}ms` }} />
      ))}
    </div>
  );
};

export const TableSkeleton = ({ rows = 5 }) => (
  <div className="space-y-3 py-2">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex items-center gap-4">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-4 w-1/6" />
        <Skeleton className="h-4 w-1/6" />
        <Skeleton className="h-4 w-1/6" />
      </div>
    ))}
  </div>
);

// ---------- controls ----------

export const SegmentedControl = ({ options, value, onChange, size = "sm" }) => (
  <div className="inline-flex max-w-full overflow-x-auto rounded-md bg-neutral-100 p-0.5 ring-1 ring-inset ring-line">
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        onClick={() => onChange(o.value)}
        className={`whitespace-nowrap rounded-[5px] font-medium transition-colors ${size === "sm" ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-13"} ${
          value === o.value ? "bg-white text-fg shadow-card ring-1 ring-line" : "text-neutral-500 hover:text-fg"
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

// ---------- charts ----------

export const ChartTooltip = ({ active, payload, label, formatters = {}, labelFormatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="min-w-[160px] rounded-lg border border-line bg-white px-3 py-2.5 shadow-lg">
      <p className="mb-1.5 text-xs font-medium text-muted">{labelFormatter ? labelFormatter(label, payload) : label}</p>
      <div className="space-y-1">
        {payload.map((p) => (
          <div key={p.dataKey} className="flex items-center justify-between gap-4 text-13">
            <span className="flex items-center gap-2 text-neutral-600">
              <span className="h-2 w-2 rounded-full" style={{ background: p.color || p.payload?.fill }} />
              {p.name}
            </span>
            <span className="font-semibold tabular-nums text-fg">
              {formatters[p.dataKey] ? formatters[p.dataKey](p.value) : p.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const Legend = ({ items }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
    {items.map((i) => (
      <span key={i.label} className="flex items-center gap-1.5 text-xs text-neutral-600">
        <span className={`h-2 w-2 ${i.line ? "h-0.5 w-3" : "rounded-full"}`} style={{ background: i.color }} />
        {i.label}
      </span>
    ))}
  </div>
);

export const axisProps = {
  tickLine: false,
  axisLine: false,
  tick: { fontSize: 11, fill: neutral[500] },
};
